import { HttpClientRequest, HttpClientResponse } from '@effect/platform';
import { Data, Effect, Schema } from 'effect';
import { CognitoHttpClient } from 'services/httpClient';
import { getRuntimeConfig } from 'helpers/runtimeConfig';
import * as ResponseSchemas from 'services/schemata/response';

type ValidatedHttpClientResponse<A> = HttpClientResponse.HttpClientResponse & {
  json: Effect.Effect<A, Effect.Effect.Error<HttpClientResponse.HttpClientResponse['json']>>;
};

export class ValidatedResponseError<E> extends Data.TaggedError('ValidatedResponseError')<{
  readonly response: HttpClientResponse.HttpClientResponse;
  readonly validatedBody: E;
  readonly status: number;
}> {}

export class ThirdPartyApi extends Effect.Service<ThirdPartyApi>()('ThirdPartyApi', {
  dependencies: [CognitoHttpClient.ThirdParty],
  effect: Effect.gen(function* () {
    const { HttpClient } = yield* CognitoHttpClient;
    return {
      getCountry: () =>
        Effect.gen(function* () {
          // for envs other then production key is not used to save quota
          const key = getRuntimeConfig().env.REACT_APP_GEOIP_SECRET_KEY;
          const authParams = key ? `?key=${key}` : '';
          return yield* HttpClient.get(`https://ipapi.co/json/${authParams}`);
        }),
    };
  }),
}) {}

export default class Api extends Effect.Service<Api>()('Api', {
  dependencies: [CognitoHttpClient.Live],
  effect: Effect.gen(function* () {
    const { HttpClient } = yield* CognitoHttpClient;

    const validateResponseSchema = <A, I, R, A2, I2, R2>(
      successSchema: Schema.Schema<A, I, R>,
      errorSchema: Schema.Schema<A2, I2, R2>,
    ) =>
      Effect.flatMap((response: HttpClientResponse.HttpClientResponse) =>
        HttpClientResponse.matchStatus(response, {
          '2xx': (response) =>
            HttpClientResponse.schemaBodyJson(successSchema)(response).pipe(
              Effect.tapError((error) =>
                error._tag === 'ParseError' ? Effect.log(`Parse error: ${error.message}`) : Effect.void,
              ),
              Effect.catchTag('ParseError', (_error) => Effect.succeed(response as ValidatedHttpClientResponse<A>)),
              Effect.map((_validatedData) => response as ValidatedHttpClientResponse<A>), // successful response schema
            ),
          '4xx': (response) =>
            HttpClientResponse.schemaBodyJson(errorSchema)(response).pipe(
              Effect.tapError((error) =>
                error._tag === 'ParseError' ? Effect.log(`Parse error: ${error.message}`) : Effect.void,
              ),
              Effect.flatMap((validatedErrorBody) =>
                Effect.fail(
                  new ValidatedResponseError({
                    response,
                    validatedBody: validatedErrorBody,
                    status: response.status,
                  }),
                ),
              ),
              Effect.catchTag('ParseError', (_error) =>
                Effect.fail(
                  new ValidatedResponseError({
                    response,
                    validatedBody: {} as A2, // this is totally wrong
                    status: response.status,
                  }),
                ),
              ),
            ),
          '5xx': (response) =>
            Effect.fail(
              new ValidatedResponseError({
                response,
                validatedBody: { error: `Request failed with ${response.status} status` } as A2,
                status: response.status,
              }),
            ),
          orElse: (response) => Effect.succeed(response as ValidatedHttpClientResponse<A>),
        }),
      );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getRequest =
      <A, I, R, A2, I2, R2>(successSchema: Schema.Schema<A, I, R>, errorSchema: Schema.Schema<A2, I2, R2>) =>
      (url: string, options?: HttpClientRequest.Options.NoUrl) =>
        Effect.gen(function* () {
          const response = yield* HttpClient.get(url, options).pipe(validateResponseSchema(successSchema, errorSchema));

          return response;
        });

    const postRequest =
      <A, I, R, A2, I2, R2>(successSchema: Schema.Schema<A, I, R>, errorSchema: Schema.Schema<A2, I2, R2>) =>
      (url: string, options?: HttpClientRequest.Options.NoUrl) =>
        Effect.gen(function* () {
          const response = yield* HttpClient.post(url, options).pipe(
            validateResponseSchema(successSchema, errorSchema),
          );
          return response;
        });

    const patchRequest =
      <A, I, R, A2, I2, R2>(successSchema: Schema.Schema<A, I, R>, errorSchema: Schema.Schema<A2, I2, R2>) =>
      (url: string, options?: HttpClientRequest.Options.NoUrl) =>
        Effect.gen(function* () {
          const response = yield* HttpClient.patch(url, options).pipe(
            validateResponseSchema(successSchema, errorSchema),
          );
          return response;
        });

    return {
      sendCode: () =>
        postRequest(ResponseSchemas.SendCodeResponse, ResponseSchemas.GenericResponseError)('/auth/phone/send-code'),
      updatePhone: (options: HttpClientRequest.Options.NoUrl) =>
        postRequest(ResponseSchemas.UpdatePhoneResponse, ResponseSchemas.GenericResponseError)(
          '/auth/phone/update',
          options,
        ),
      verifyCode: (options: HttpClientRequest.Options.NoUrl) =>
        postRequest(ResponseSchemas.VerifyCodeResponse, ResponseSchemas.GenericResponseError)(
          '/auth/phone/verify',
          options,
        ),
      skipPhoneUpdate: (options: HttpClientRequest.Options.NoUrl) =>
        patchRequest(ResponseSchemas.SkipPhoneUpdateResponse, ResponseSchemas.GenericResponseError)(
          '/auth/phone/skip-validation',
          options,
        ),
      postAuth: (options: HttpClientRequest.Options.NoUrl) =>
        getRequest(ResponseSchemas.PostAuthResponse, ResponseSchemas.PostAuthError)('/auth/postLogin', options),
    };
  }),
}) {}
